import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import currency from "currency.js";

import CircularProgress from "../../../generic/CircularProgress";
import { getPaymentRequests } from "../../../../services/backend/PaymentRequestLegalService";
import { getContractField } from "../../../../functions";
import { PAYMENT_REQUEST_STATUS_UI } from "../../../../constants";
import { summarizeExecution } from "./paymentExecution";
import { toDateValue } from "./paymentReviewUi";

// Facturación y estado financiero del contrato jurídico, dentro de la vista de
// detalle (C8). Reemplaza al cuadro de "plan de pagos": en el contrato con
// persona jurídica no hay cuotas previstas, hay facturas presentadas.
//
// Las dos tarjetas leen la MISMA fuente (las solicitudes de pago del contrato)
// y no un acumulado guardado en el contrato: el detalle es la lista y el
// resumen es su suma, así que no pueden contradecirse.

const fmt = (value) => currency(value || 0, { precision: 0 }).format();

const formatDate = (value) => {
  const date = toDateValue(value);
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("es-CO") : "—";
};

const percent = (rate) => `${Math.round((rate || 0) * 1000) / 10}%`;

const SummaryRow = ({ label, value, color, bold }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" color={color} fontWeight={bold ? 600 : 400}>
      {value}
    </Typography>
  </Box>
);

const ContractLegalBilling = ({ contract }) => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const contractCode = contract?.contractCode;
  const [requests, setRequests] = useState(null);

  useEffect(() => {
    (async () => {
      const list = await getPaymentRequests(contractCode, user);
      setRequests(list || []);
    })();
    // eslint-disable-next-line
  }, [contractCode]);

  // Valor VIGENTE del contrato: con novedades aplicadas, igual que el resto de
  // la vista y que la advertencia de exceso del formulario de solicitud.
  const budget = useMemo(
    () => Number(getContractField(contract?.news, contract)("contractBudget")) || 0,
    [contract]
  );

  const summary = useMemo(() => summarizeExecution(budget, requests), [budget, requests]);

  const loading = requests === null;

  return (
    <>
      <Grid item xs={12} md={6}>
        <Card variant="outlined" sx={{ height: "100%" }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Estado financiero
            </Typography>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <SummaryRow label="Valor vigente del contrato" value={fmt(summary.budget)} />
                <SummaryRow
                  label={`Facturación aprobada (${summary.approvedCount})`}
                  value={fmt(summary.approved)}
                  color="success.main"
                />
                {/* Subconjunto de lo aprobado que ya llegó a Caja: no suma
                    aparte, solo distingue causado de pagado (D7). */}
                <SummaryRow
                  label={`  de las cuales pagadas (${summary.paidCount})`}
                  value={fmt(summary.paid)}
                />
                <SummaryRow
                  label={`Facturación en trámite (${summary.inReviewCount})`}
                  value={fmt(summary.inReview)}
                />
                <Box sx={{ borderTop: 1, borderColor: "divider", mt: 1, pt: 1 }}>
                  <SummaryRow
                    label="Saldo del contrato"
                    value={fmt(summary.balance)}
                    color={summary.overBudget ? "error.main" : undefined}
                    bold
                  />
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Ejecución financiera
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {percent(summary.executionRate)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    color={summary.overBudget ? "error" : "success"}
                    value={Math.min(100, summary.executionRate * 100)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {percent(summary.committedRate)} comprometido incluyendo lo que está en
                    trámite.
                  </Typography>
                </Box>

                {summary.overBudget && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    La facturación supera el valor vigente del contrato en{" "}
                    {fmt(Math.abs(summary.balance))}.
                  </Alert>
                )}
                {summary.draft > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    No se incluyen {fmt(summary.draft)} en borradores sin presentar.
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="subtitle1">Facturación</Typography>
              <Button
                size="small"
                onClick={() => navigate(`/contracts/legal/${contractCode}/payments`)}
              >
                Ver solicitudes de pago
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : requests.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Todavía no hay solicitudes de pago presentadas para este contrato.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Presentada</TableCell>
                    <TableCell align="right">Valor facturado</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Aprobada</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((request) => {
                    const statusUi = PAYMENT_REQUEST_STATUS_UI[request.status] || {};
                    return (
                      <TableRow key={request.requestId} hover>
                        <TableCell>{request.seq}</TableCell>
                        <TableCell>{formatDate(request.submittedAt)}</TableCell>
                        <TableCell align="right">
                          {request.invoiceAmount ? fmt(request.invoiceAmount) : "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusUi.label || request.status}
                            color={statusUi.color}
                            sx={statusUi.sx}
                            icon={statusUi.Icon ? <statusUi.Icon /> : undefined}
                          />
                        </TableCell>
                        <TableCell>{formatDate(request.approvedAt)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Ver solicitud">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/contracts/legal/${contractCode}/payments/${request.requestId}`
                                )
                              }
                            >
                              <DescriptionIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Grid>
    </>
  );
};

export default ContractLegalBilling;
